import React from 'react';
import * as _ from 'lodash';
import InfinityScrollLoad from '../../InfinityScrollLoad';
import { FieldTreeService } from '../../../services/FieldTrees';
import { checkSelectedAll } from '../../../utilities/commonUtil';
import {
  DROPDOWN_SEARCH_DEBOUNCE_TIMEOUT,
  REQUEST_PARAMS
} from '../../../utilities/constants';
import { messageUtility } from '../../../utilities/messageUtility';

/**
 * @description ツリー職種ドロップダウン
 *              施工体系図画面で使用
 * @param {string} fieldId 現場ID
 * @param {string} foreignKey ドロップダウンキー
 * @param {function} callback 職種設定メソッド
 * @param {boolean} refreshFlag 検索条件クリアフラグ
 */
export class FieldTreeJobsDropdown extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      opened: false,
      selectAll: false,
      selected: [],
      sorted: {},
      options: {},
      condition: {
        name: '',
      },
      isLoading: false, // ロード中フラグ
      loaded: false,
    };

    // ユニークID生成
    this.id = _.uniqueId();
    this.targetId = `infinityscroll-target-field-tree-operatable-ids-multi-dropdown-${this.id}`;
    this.search = this.search.bind(this);
    this.isSelected = this.isSelected.bind(this);
    this.handleOpen = this.handleOpen.bind(this);
    this.setCondition = this.setCondition.bind(this);
    this.handleSelectedRow = this.handleSelectedRow.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.getFieldTreeJobs = this.getFieldTreeJobs.bind(this);
    this.parent = React.createRef();
    this.handleClickOutsideOfComponent = this.handleClickOutsideOfComponent.bind(
      this
    );
    this.searchTimer;
  }

  componentDidMount() {
    this.getFieldTreeJobs(() => {
      // 職種一覧のロード完了後に親コンポーネントから渡された選択済みのjob_idをstate.selectedに反映する
      this.setState({ selected: _.cloneDeep(this.props.selected) });
    });
  }

  // eslint-disable-next-line no-unused-vars
  componentDidUpdate(prevProps, state) {
    if (this.props.jvCompanyBranchId !== prevProps.jvCompanyBranchId) {
      this.setState({ selected: [], loaded: false }, () => {
        // JVを変更した場合、選択中のjob_idをクリアする
        this.getFieldTreeJobs(() => {}, true);
      });
    }
    if (this.state.opened) {
      // ドロップダウンが可視状態になった場合は、documentにコンポーネント外をclickしたかのイベントを追加する
      document.addEventListener(
        'click',
        this.handleClickOutsideOfComponent,
        false
      );
    }
    if (!this.state.opened) {
      // ドロップダウンが不可視状態になった場合は、documentのclickイベントを削除する
      document.removeEventListener(
        'click',
        this.handleClickOutsideOfComponent,
        false
      );
    }

    // 元のcomponentWillReceivePropsで実装されていたもの
    if (
      prevProps.refreshFlag != this.props.refreshFlag &&
      this.props.refreshFlag === true
    ) {
      let stateValue = _.cloneDeep(this.state);
      stateValue.opened = false;
      stateValue.selected = [];
      stateValue.selectAll = false;
      stateValue.condition.name = '';
      this.setState(stateValue, () => this.props.resetRefreshFlag());
      this.search();
    } else if (
      !_.isEqual(prevProps.selected, this.props.selected) &&
      !_.isEqual(this.props.selected, this.state.selected)
    ) {
      this.setState({
        selected: _.cloneDeep(this.props.selected),
      });
    }
  }

  handleClickOutsideOfComponent = e => {
    // コンポーネント内クリックの場合は無視する
    if (
      !_.isNull(this.parent.current) &&
      this.parent.current.contains(e.target)
    ) {
      return;
    }
    // コンポーネント外クリックの場合はドロップダウンを不可視とする
    // イベントはstateにfalseが設定された際にremoveされる
    this.setState({
      opened: false,
      selected: _.cloneDeep(this.props.selected),
    });
  };

  handleOpen = () => {
    this.setState(
      prevState => ({
        selectAll: !prevState.opened
          ? checkSelectedAll(
              _.map(prevState.selected, 'id'),
              _.map(this.state.options, 'id')
            )
          : prevState.selectAll,
        opened: !this.state.opened,
        loaded: false,
      }),
      () => {
        this.getFieldTreeJobs(() => {}, true);
      }
    );
  };

  setCondition = e => {
    let condition = this.state.condition;
    condition.name = e.target.value;
    this.setState({ condition }, () => {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.search();
      }, DROPDOWN_SEARCH_DEBOUNCE_TIMEOUT);
    });
  };

  search = () => {
    this.setState(
      {
        sorted: {},
        options: {},
        loaded: false,
      },
      () => {
        this.getFieldTreeJobs(() => {}, true);
      }
    );
  };

  handleSelectedRow = (job, checked) => {
    let selected = this.state.selected;
    if (checked) {
      selected.push(job);
    } else {
      selected = [];
      _.each(this.state.selected, item => {
        if (item.id !== job.id) {
          selected.push(item);
        }
      });
    }
    const selectAll = checkSelectedAll(_.map(selected, 'id'), _.map(this.state.options, 'id'));
    this.setState({ selected, selectAll }, () => {
      this.handleChange();
    });
  };

  handleChange = () => {
    let selected = this.state.selected;

    if (_.isUndefined(this.props.foreignKey)) {
      throw new Error('FieldTreeJobs.jsx handleChange');
    }
    this.props.set(this.props.foreignKey, selected);
  };

  handleSelectedAll = e => {
    let selected = [];
    let selectAll = false;
    if (e.target.checked) {
      _.each(this.state.sorted, job => {
        selected.push(job);
      });
      selectAll = true;
    }
    this.setState({ selected, selectAll }, () => {
      this.handleChange();
    });
  };

  isSelected = (jobId) => _.some(this.state.selected, item => item.id === jobId);

  getFieldTreeJobs(notify, search = false) {
    if (this.state.isLoading || this.state.opened === false || this.state.loaded) {
      notify();
      return;
    }
    this.setState({ isLoading: true }, () => {
      let params = {
        limit: REQUEST_PARAMS.parentLimit,
        offset: search ? 0 : _.size(this.state.options),
        name: this.state.condition.name,
        jv_company_branch_id: this.props.jvCompanyBranchId
          ? this.props.jvCompanyBranchId
          : null,
      };

      FieldTreeService.treeJobList(this.props.fieldId, params).then(
        response => {
          if (response.result) {
            const jobs = response.data.entities.jobs;
            if (_.isEmpty(jobs)) {
              this.setState({
                sorted: search ? {} : this.state.sorted,
                isLoading: false,
                options: search ? {} : this.state.options,
                loaded: true,
                selectAll: search ? false : this.state.selectAll,
              });
            } else {
              let options = this.state.options;
              if (search) {
                options = {};
              }
              _.each(jobs, job => {
                options[job.id] = job;
              });
              let lists = _.cloneDeep(options);
              const sorted = _.orderBy(
                lists,
                ['order_number', 'updated_at'],
                ['asc', 'desc']
              );
              const selectAll = checkSelectedAll(_.map(this.state.selected, 'id'), _.map(options, 'id'));
              this.setState({
                sorted,
                options,
                isLoading: false,
                selectAll,
                loaded: false,
              });
            }
          } else {
            this.setState({ isLoading: false });
          }
          notify();
        }
      );
    });
  }

  render() {
    return (
      <div className='form-element__element'>
        <div
          id='form-dropdown'
          className='dropdown-multi-select'
          ref={this.parent}
        >
          <div className='dropdown-multi-select__header'>
            <div
              id='form-dropdown-btn'
              href=''
              className={
                this.state.opened ? 'dropdown-select show' : 'dropdown-select'
              }
              onClick={() => this.handleOpen()}
            >
              <span className='dropdown-select__txt'>
                {_.isEmpty(this.state.selected)
                  ? '職種を選択'
                  : _.join(_.map(this.state.selected, 'name'), ',')
                }
              </span>
            </div>
          </div>
          <div
            id='form-dropdown-list'
            className={
              this.state.opened
                ? 'dropdown dropdown--dropdown-multi-select show'
                : 'dropdown dropdown--dropdown-multi-select'
            }
          >
            <div className='dropdown__item'>
              <div className='dropdown-multi-select__item'>
                <i className='i-search i-2x' />
                <input
                  type='text'
                  className='--col-xs-12 --col-md-12 --col-lg-12 --col-xl-12'
                  onChange={e => this.setCondition(e)}
                  value={this.state.condition.name}
                  placeholder={messageUtility.getMessage('I-000-090')}
                />
              </div>
              <div className='dropdown-multi-select__item'>
                <div className='dropdown-multi-select__checkall'>
                  <div className='btn-checkbox'>
                    <label htmlFor={`checkall-fieldTreeJob-${this.id}`}>
                      <input
                        id={`checkall-fieldTreeJob-${this.id}`}
                        type='checkbox'
                        checked={this.state.selectAll}
                        onChange={e => this.handleSelectedAll(e)}
                      />
                      <i className='i-square-checkbox i-2_5x' />
                    </label>
                  </div>
                  {/* ラベルクリックでラジオボタン選択のため(全選択) */}
                  <label htmlFor={`checkall-fieldTreeJob-${this.id}`}>
                    {messageUtility.getMessage('I-000-092')}
                  </label>
                </div>
              </div>
              <div className='dropdown-multi-select__scroll' id={this.targetId}>
                <ul id='dropdown-multi-select-list' className='multi-select'>
                  {_.map(this.state.sorted, (option, index) => (
                    <SelectableOption
                      key={index}
                      option={option}
                      checked={this.isSelected(option.id)}
                      targetId={this.id}
                      handleSelectedRow={this.handleSelectedRow}
                    />
                  ))}
                  <InfinityScrollLoad
                    targetId={this.targetId}
                    total={_.size(this.state.options)}
                    isLoading={this.state.isLoading}
                    callback={this.getFieldTreeJobs}
                  />
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

/**
 * 選択肢
 * @param option {object}
 * @param checked {bool}
 * @param targetId {int}
 * @param handleSelectedRow {func}
 */
const SelectableOption = ({ option, checked, targetId, handleSelectedRow }) => (
  <li className='multi-select__item'>
    <div className='checkbox'>
      <label htmlFor={`multi-select-fieldTreeJob-id-${option.id}-${targetId}`}>
        <input
          id={`multi-select-fieldTreeJob-id-${option.id}-${targetId}`}
          type='checkbox'
          checked={checked}
          onClick={() => handleSelectedRow(option, !checked)}
        />
        <i className='i-square-checkbox i-2_5x' />
      </label>
    </div>
    <div
      className='multi-select__label'
      onClick={() => handleSelectedRow(option, !checked)}
    >
      {option.name}
    </div>
  </li>
);



// WEBPACK FOOTER //
// ./src/components/dropdowns/multi/FieldTreeJobs.jsx